import React, { useEffect, useRef } from 'react';

// Cloudflare Turnstile bot protection, shared by the classic signup form and
// the /start flow's slim signup (extracted from SignUpForm, Sandy 2026-08-27).
// Paste the SITE key from Cloudflare dashboard -> Turnstile -> your widget.
// Empty string = captcha disabled: no widget is rendered and no token is sent,
// so this is safe to deploy before the Cloudflare widget exists.
//
// Was OFF 2026-10-06 to 2026-10-10 (with autoConfirmUser REQUIRE_CAPTCHA=false).
// Back ON 2026-10-10, OFF again 2026-10-14 (Sandy). Order matters: this
// frontend must be live BEFORE REQUIRE_CAPTCHA=true, and blanking this key
// while the lambda requires a token blocks every self-serve signup (no token
// is sent). Key to restore: '0x4AAAAAAECaldvNnmJ0kJXV'.
export const TURNSTILE_SITE_KEY = '';

// FAIL OPEN (2026-08-28): if the widget errors or its script never loads
// (blocked script, misconfigured hostname allowlist, Cloudflare outage), the
// form must not trap a real student — the submit gates treat this sentinel as
// "check done", and the senders skip the validationData so no garbage token
// reaches Cognito. Blocking a widget failure gains nothing anyway: bots skip
// the form entirely, so real enforcement lives in the pre-signup lambda
// (REQUIRE_CAPTCHA), which fails open on its own outages too.
export const TURNSTILE_UNAVAILABLE = 'turnstile-unavailable';
export const isRealToken = (t) => !!t && t !== TURNSTILE_UNAVAILABLE;

// className = the caller's vertical spacing, so the widget sits evenly between
// its neighbours in each form's own layout (flex gap vs stacked margins).
function TurnstileWidget({ onToken, className = '' }) {
  const containerRef = useRef(null);

  useEffect(() => {
    let widgetId = null;
    let cancelled = false;
    const deadline = Date.now() + 8000; // script-load budget before failing open

    const renderWidget = () => {
      if (cancelled) return;
      if (window.turnstile && containerRef.current) {
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          // 'normal' is a fixed 300px; narrower forms (/signup on a phone is
          // ~277px) get the 150px 'compact' box instead of overhanging.
          size: containerRef.current.clientWidth < 300 ? 'compact' : 'normal',
          callback: onToken,
          'expired-callback': () => onToken(''),
          'error-callback': () => onToken(TURNSTILE_UNAVAILABLE),
        });
      } else if (Date.now() > deadline) {
        onToken(TURNSTILE_UNAVAILABLE);
      } else {
        setTimeout(renderWidget, 200);
      }
    };

    if (!document.getElementById('cf-turnstile-script')) {
      const script = document.createElement('script');
      script.id = 'cf-turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      document.head.appendChild(script);
    }
    renderWidget();

    return () => {
      cancelled = true;
      if (widgetId !== null && window.turnstile) {
        try { window.turnstile.remove(widgetId); } catch (e) { /* already removed */ }
      }
    };
  }, [onToken]);

  return <div ref={containerRef} className={`flex justify-center ${className}`} />;
}

export default TurnstileWidget;
